import { makeAutoObservable, runInAction } from 'mobx';
import i18n from 'i18n';
import { strToFloat } from 'services/utils';
import {
  customerInternalTransactionCheck,
  customerInternalTransactionCreate,
  customerInternalTransactionConfirm,
  resendSecurityCode,
  forceChangeConfirmationTypeRequest
} from 'services/requestAgent';
import { setTimerDurationExpiresAt, updateConfirmationActionType } from '../services/authUtils';
import { BACKEND_MODULE_TYPES } from '../components/common/constants';

class BetweenAccountsStore {
  isInitialized = false;
  isLoading = false;
  isRepeatTransaction = false;
  isRedirectFromPaymentForm = false;
  isInternalTransactionChecked = false;
  isInternalTransferCompleted = false;
  isShowConfirmationPopUp = false;
  isShowSuccessPopUp = false;
  isCardAccountTopUp = false;
  isRedirectToCards = false;
  error = null;
  confirmationPopupError = null;
  temporaryError = null;
  accountFrom = null;
  accountTo = null;
  amount = null;
  totalAmount = null;
  totalAmountDetails = null;
  commission = null;
  commissionDetails = null;
  description = null;
  confirmationId = null;
  isShowingInternalActionConfirmation = false;
  isShowSwitchConfirmationMethodScreen = false;
  isShowingConfirmationTab = false;

  constructor() {
    makeAutoObservable(this);
  }

  resetStore() {
    this.isInitialized = false;
    this.isLoading = false;
    this.isRepeatTransaction = false;
    this.isRedirectFromPaymentForm = false;
    this.isInternalTransactionChecked = false;
    this.isInternalTransferCompleted = false;
    this.isShowConfirmationPopUp = false;
    this.isShowSuccessPopUp = false;
    this.isRedirectToCards = false;
    this.error = null;
    this.confirmationPopupError = null;
    this.accountFrom = null;
    this.accountTo = null;
    this.amount = null;
    this.totalAmount = null;
    this.totalAmountDetails = null;
    this.commission = null;
    this.commissionDetails = null;
    this.description = null;
    this.confirmationId = null;
    this.isShowingInternalActionConfirmation = false;
    this.isShowSwitchConfirmationMethodScreen = false;
    this.isShowingConfirmationTab = false;
  }

  setError(error) {
    this.error = error;
  }

  setTemporaryError(error) {
    this.temporaryError = error;
    if (error) {
      // Clear temporary error after 5 seconds
      setTimeout(() => {
        runInAction(() => {
          if (this.temporaryError?.code === error?.code) {
            this.temporaryError = null;
          }
        });
      }, 5000);
    }
  }

  setIsRepeatTransactionStatus(status) {
    this.isRepeatTransaction = status;
  }

  setIsRedirectFromPaymentFormStatus(status) {
    this.isRedirectFromPaymentForm = status;
  }

  setIsShowingInternalActionConfirmation(status) {
    this.isShowingInternalActionConfirmation = status;
  }

  setIsShowSwitchConfirmationMethodScreen(status) {
    this.isShowSwitchConfirmationMethodScreen = status;
  }

  setFromToAccounts(from, to) {
    this.accountFrom = from;
    this.accountTo = to;

    if (!from || !to) {
      this.error = i18n.getMessage('betweenAccounts.error.sameCurrencyAccounts');
    } else {
      this.error = null;
    }

    if (!this.isInitialized) {
      this.isInitialized = true;
    }
  }

  setAmount(amount) {
    this.amount = amount;
  }

  setCommission(commission, commissionDetails = null) {
    this.commission = commission;
    this.commissionDetails = commissionDetails;
  }

  setDescription(description) {
    this.description = description;
  }

  setIsInternalTransferCompleted(status) {
    this.isInternalTransferCompleted = status;
  }

  setIsCardAccountTopUp(status) {
    this.isCardAccountTopUp = status;
  }

  setIsRedirectToCards(status) {
    this.isRedirectToCards = status;
  }

  setIsShowingConfirmationTab(status) {
    this.isShowingConfirmationTab = status;
  }

  clearConfirmationPopupError() {
    this.confirmationPopupError = null;
  }

  async checkInternalTransaction(customerNumber) {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        commission,
        commissionDetails,
        total_amount: totalAmount,
        totalAmountDetails
      } = await customerInternalTransactionCheck(customerNumber, {
        sender_wallet: this.accountFrom.wallet_number,
        recipient_wallet: this.accountTo.wallet_number,
        amount: strToFloat(this.amount),
        comment: this.description
      });

      runInAction(() => {
        this.isInternalTransactionChecked = true;
        this.commission = commissionDetails?.amount || commission;
        this.commissionDetails = commissionDetails;
        this.totalAmount = totalAmountDetails?.amount || totalAmount;
        this.totalAmountDetails = totalAmountDetails;
      });
    } catch (err) {
      if (err.code === 'EXCHANGE_RATE_MIN_VALUE') {
        let newError = {};
        newError.fields = {
          amount: err.code
        };
        runInAction(() => {
          this.error = newError;
          this.isInternalTransactionChecked = false;
        });
        return;
      }
      runInAction(() => {
        this.error = err;
        this.isInternalTransactionChecked = false;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async createInternalTransaction(customerNumber) {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await customerInternalTransactionCreate(customerNumber, {
        sender_wallet: this.accountFrom.wallet_number,
        recipient_wallet: this.accountTo.wallet_number,
        amount: strToFloat(this.amount),
        comment: this.description
      });

      setTimerDurationExpiresAt(expiresAt);
      updateConfirmationActionType(confirmationType);

      runInAction(() => {
        this.isShowingInternalActionConfirmation = true;
        this.confirmationId = confirmationId;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async resendTransactionSecurityCode() {
    try {
      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await resendSecurityCode(BACKEND_MODULE_TYPES.PAYMENT, this.confirmationId);

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    }
  }

  async verifyInternalTransaction(customerNumber, securityCode) {
    try {
      runInAction(() => {
        this.isLoading = false;
      });

      await customerInternalTransactionConfirm(customerNumber, {
        code: securityCode,
        confirmation_id: this.confirmationId
      });

      runInAction(() => {
        this.isShowingInternalActionConfirmation = false;
        this.isShowSuccessPopUp = true;
        this.isInternalTransferCompleted = true;
      });
    } catch (err) {
      runInAction(() => {
        // Handle push notification rejection
        if (err?.code === 'CONFIRMATION_REJECTED') {
          this.confirmationPopupError = err;
          this.isShowingInternalActionConfirmation = false;
          // Reset to form state
          this.confirmationId = null;
          // Show toast notification about rejection
          this.setTemporaryError({
            code: 'CONFIRMATION_REJECTED',
            message: i18n.getMessage ? i18n.getMessage('error.confirmationRejected') : 'Request was declined'
          });
        } else if (err?.code !== 'CONFIRMATION_PENDING_ACTION') {
          // Don't update error for CONFIRMATION_PENDING_ACTION - just continue polling
          this.confirmationPopupError = err;
        }
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  forceSwitchToAnotherConfirmationType = async (confirmationType) => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await forceChangeConfirmationTypeRequest(this.confirmationId, confirmationType, BACKEND_MODULE_TYPES.PAYMENT);

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  };
}

export default new BetweenAccountsStore();
